import { Button, Col, Icon, Row } from '@zoom/components';
import { addDays, format, isAfter, isSameDay, startOfToday } from 'date-fns';
import { FormApi } from 'final-form';
import { TFunction } from 'i18next';
import { equals } from 'ramda';
import React from 'react';
import { Field, Form, FormRenderProps, FormSpy } from 'react-final-form';

import { useAuth } from '../../app/AuthorizationProvider';
import { DatePickerField } from '../../components/DatePicker';
import { finalFormDeBouncedInputHelper } from '../../components/Inputs/DeBouncedInput';
import { max255chars, required } from '../../utils/validations';
import { composeValidators } from '../../utils/validations/composeValidators';
import { withinRange } from '../../utils/validations/withinRange';
import {
  REFRESH_BUTTON_TAG,
  SEARCH_ADHERENCE_HISTORY_HEADER_CONTAINER_TAG,
  TOGGLE_FILTER_BUTTON_TAG,
} from './constants';

import type { AdherenceHistoryHeaderFilterType } from './types';

interface AdherenceHistoryHeaderProps {
  isSidebarOpen: boolean;
  minStartDate: string;
  lastDataImport: string | null;
  onRefresh: () => void;
  onSubmit: (values: AdherenceHistoryHeaderFilterType, formProps: FormApi<AdherenceHistoryHeaderFilterType>) => void;
  t: TFunction;
  toggleSidebar: () => void;
}

export const AdherenceHistoryHeader: React.FC<AdherenceHistoryHeaderProps> = ({
  isSidebarOpen,
  minStartDate,
  lastDataImport,
  onRefresh,
  onSubmit,
  t,
  toggleSidebar,
}) => {
  const { localeObj } = useAuth();

  const defaultDateTime = startOfToday();

  const isDatePickerDisabled = minStartDate === '0';

  const minStartDatePickerDate = new Date(minStartDate);

  const maxEndDatePickerDate = addDays(defaultDateTime, 1); // today + 1 day as open interval for DatePicker

  const nameField = finalFormDeBouncedInputHelper({
    label: t('person.name'),
    dataTestId: 'search-form:input-text--agentName',
    type: 'text',
  });

  const renderAdherenceHistoryHeaderFilterForm = (props: FormRenderProps<AdherenceHistoryHeaderFilterType>) => {
    const { startDateTime, endDateTime } = props.values;

    return (
      <form>
        <FormSpy
          subscription={{ values: true }}
          onChange={({ values }) => {
            if (
              isSameDay(values.startDateTime, props.values.startDateTime) &&
              isSameDay(values.endDateTime, props.values.endDateTime) &&
              values.agentName === props.values.agentName
            ) {
              // skip submitting same values (prevents error on initial load)
              return;
            }

            const startCondition =
              isAfter(values.startDateTime, minStartDatePickerDate) ||
              isSameDay(values.startDateTime, minStartDatePickerDate);

            const endCondition =
              isAfter(values.endDateTime, values.startDateTime) || isSameDay(values.endDateTime, values.startDateTime);

            if (startCondition && endCondition) {
              props.handleSubmit();
            }
          }}
        />
        <Row className="list_filter__wrapper" data-test-id={SEARCH_ADHERENCE_HISTORY_HEADER_CONTAINER_TAG}>
          <Col sm="auto">
            <Field name="agentName" validate={max255chars} render={nameField} />
          </Col>
          <Col sm="auto">
            <Field
              isEqual={equals}
              name="startDateTime"
              key={`start${endDateTime?.toISOString()}`}
              validate={required}
              component={DatePickerField({
                dataTestId: 'search-form:input-date-picker--startDateTime',
                dateFormat: t('date.formats.default'),
                disabled: isDatePickerDisabled,
                endDate: endDateTime,
                label: t('date.dateFrom'),
                maxDate: defaultDateTime,
                minDate: minStartDatePickerDate,
                selectsStart: true,
                startDate: startDateTime,
              })}
            />
          </Col>
          <Col sm="auto">
            <Field
              isEqual={equals}
              name="endDateTime"
              key={`end${startDateTime?.toISOString()}`}
              validate={composeValidators(required, withinRange(startDateTime, defaultDateTime))}
              component={DatePickerField({
                dataTestId: 'search-form:input-date-picker--endDateTime',
                dateFormat: t('date.formats.default'),
                disabled: isDatePickerDisabled,
                endDate: endDateTime,
                label: t('date.dateTo'),
                maxDate: maxEndDatePickerDate,
                minDate: startDateTime,
                selectsEnd: true,
                startDate: startDateTime,
              })}
            />
          </Col>
          <FormSpy subscription={{ dirty: true }}>
            {({ dirty, form: { reset } }) =>
              dirty && (
                <Col sm="auto">
                  <Button
                    kind="icon-bordered"
                    data-test-id="search-form:button-reset"
                    onClick={() => reset()}
                    title={t('filter.resetFilter')}
                  >
                    <Icon kind="cross" size={16} color="black2" />
                  </Button>
                </Col>
              )
            }
          </FormSpy>
        </Row>
      </form>
    );
  };

  return (
    <Row>
      <Form
        data-test-id="adherence-history-header:search-form"
        initialValues={{ startDateTime: defaultDateTime, endDateTime: defaultDateTime }}
        initialValuesEqual={equals}
        onSubmit={onSubmit}
        render={renderAdherenceHistoryHeaderFilterForm}
      />
      <Col>
        <div className="d-flex ml-4">
          <Button
            onClick={onRefresh}
            kind="icon-bordered"
            title={t('filter.refresh')}
            data-test-id={REFRESH_BUTTON_TAG}
          >
            <Icon color="#239ed8" kind="refresh" size={16} />
          </Button>
          {lastDataImport && (
            <div className="adherence-last-updated">
              <span className="color-grey3">{t('scheduleAdherence.dataAsOf')}</span>
              {format(new Date(lastDataImport), t('date.formats.monthAndTime'), { locale: localeObj })}
            </div>
          )}
        </div>
      </Col>
      <div className="adherence-history__search-header-right-side-wrapper">
        <Button
          active={isSidebarOpen}
          className="button--icon--left-hand-side"
          color="info"
          data-test-id={TOGGLE_FILTER_BUTTON_TAG}
          onClick={toggleSidebar}
        >
          <Icon color="#239ed8" kind="bars" size={20} />
          {t('filter.filters')}
        </Button>
      </div>
    </Row>
  );
};
